import request from "@/data/api/request";
import { Dto } from "./dto";
import type IDtoList from "./dto_list.d";
import { type DataListResponse } from "./dto_list.d";
import { type JSONData } from "./dto.d";
import { IPageInfo, IPagination } from "./pagination.d";
import { IQuery } from "./query.d";
import { PageInfo, Pagination } from "./pagination";
import { Query } from "./query";

export function paginationAndQueriesToCacheKey(
  pag: IPagination,
  queries: IQuery[],
): string {
  return `${Pagination.ToCacheKey(pag)}-${Query.ToCacheKey(queries)}`;
}

export default class DtoList implements IDtoList {
  /// ///////////////////////////////////////
  /// //////// PRIVATE VARIABLES ////////////
  /// ///////////////////////////////////////
  _path = "example/{example_id}/path";
  _pathVars: {
    [key: string]: string;
  } = {};

  /// ///////////////////////////////////////
  /// ///////// PUBLIC VARIABLES ////////////
  /// ///////////////////////////////////////
  active: string | null = "";
  list: Dto[] = [];
  pages: Record<string, Dto[]> = {};
  pageInfo = new PageInfo();

  queries: Query[] = [];
  queriedList: Dto[] = [];

  /// ///////////////////////////////////////
  /// ////////////// METHODS ////////////////
  /// ///////////////////////////////////////

  get getPathKeys(): string[] {
    // Get path keys
    return (this._path.match(/{[^}]*}/g) ?? []).map((key) =>
      key.replace("{", "").replace("}", ""),
    );
  }

  buildPathObject(
    pathVars?:
      | string
      | string[]
      | {
          [key: string]: string;
        },
  ): {
    [key: string]: string;
  } {
    // Failsafe
    if (typeof pathVars === "string") {
      pathVars = [pathVars];
    } else if (pathVars === undefined || pathVars === null) {
      return this._pathVars;
    }

    // Build the path object
    this._pathVars = this.getPathKeys.reduce(
      (acc, key, i) => {
        if (typeof pathVars === "object" && !Array.isArray(pathVars)) {
          if (pathVars[key] === undefined) {
            throw new Error(`Missing path variable: ${key}`);
          } else {
            acc[key] = pathVars[key];
          }
        } else if (pathVars.length < i + 1) {
          throw new Error(`Missing path variable: ${key}`);
        } else {
          acc[key] = pathVars[i];
        }

        return acc;
      },
      {} as {
        [key: string]: string;
      },
    );

    return this._pathVars;
  }

  buildPath(
    pathVars?:
      | string
      | string[]
      | {
          [key: string]: string;
        },
  ): string {
    let path = this._path;

    // Format path variables
    this._pathVars = this.buildPathObject(pathVars);

    // Replace path variables
    Object.entries(this._pathVars).forEach(([key, value]) => {
      path = path.replace(`{${key}}`, value);
    });

    return this._path;
  }

  async delete(entities?: Dto[]): Promise<void> {
    // Get entities
    if (entities === undefined) {
      entities = this.list;
    }

    // Loop through entities
    await Promise.all(
      entities.map(async (entity: Dto) => {
        await entity.delete(this._pathVars);
      }),
    );
  }

  async getAll(
    pathVars?: string | string[] | { [key: string]: string },
  ): Promise<Dto[]> {
    // Build path
    const path = this.buildPath(pathVars);

    // Get data
    const data: DataListResponse =
      (await request({
        url: path,
        method: "get",
      })) ?? [];

    // Handle null response (e.g., from 401 errors)
    if (data === null) {
      console.info("Request returned null, likely due to authentication error");
      return [];
    }

    // Unpack
    const { items } = data;
    if (data.active !== undefined) {
      this.active = data.active;
    }

    // Exists
    if (typeof items !== "object") {
      throw new Error("Incorrect data");
    } else if (items === null) {
      console.info("No items found");
      return [];
    }

    // Build
    items.forEach((item: JSONData) => {
      // Create dto
      const dto = this._buildInstance(item);

      // Reinsert into list
      this.reinsert(dto);
    });

    return this.list;
  }

  async getPaginated(
    pathVars?: string | string[] | { [key: string]: string },
    pag?: Pagination,
  ): Promise<{
    items: Dto[];
    pagination: Pagination;
  }> {
    // Build path
    const path = this.buildPath(pathVars);

    // Set pagination
    if (pag !== undefined) {
      this.pageInfo.fromPagination(pag);
    }

    // Get data
    const data: DataListResponse =
      (await request({
        url: path + "?pagination=" + JSON.stringify(this.pageInfo),
        method: "get",
        data: {
          pagination: this.pageInfo,
        },
      })) ?? [];

    // Handle null response (e.g., from 401 errors)
    if (data === null) {
      console.info("Request returned null, likely due to authentication error");
      return {
        items: [],
        pagination: this.pageInfo.toPagination(),
      };
    }

    // Unpack
    const { items, pageInfo } = data;
    if (data.active !== undefined) {
      this.active = data.active;
    }

    // Exists
    if (typeof items !== "object" || items === null) {
      throw new Error("Incorrect data");
    }

    // Get page info
    if (pageInfo !== undefined) {
      this.pageInfo.fromObject(pageInfo);
    }

    // Build
    const dtoList = [] as Dto[];
    items.forEach((item: JSONData) => {
      // Create dto
      const dto = this._buildInstance(item);

      // Set page number
      dto._page = this.pageInfo.pageNumber;

      // Reinsert into list, cache and dtoList
      this.reinsert(dto);
      dtoList.push(dto);
    });

    return {
      items: dtoList,
      pagination: this.pageInfo.toPagination(),
    };
  }

  async query(
    queries: Query[],
    pagination?: Pagination,
  ): Promise<{
    items: Dto[];
    pagination: IPagination;
  }> {
    // Build path
    const path = this.buildPath();

    // Set pagination
    if (pagination !== undefined) {
      this.pageInfo.fromPagination(pagination);
    }

    const data: DataListResponse = await request({
      url:
        path +
        "?queries=" +
        Query.ToQueryParams(queries) +
        "&pagination=" +
        this.pageInfo.toQueryParams(),
      method: "get",
      data: {
        queries,
        pagination,
      },
    });

    // Handle null response (e.g., from 401 errors)
    if (data === null) {
      console.info("Request returned null, likely due to authentication error");
      return {
        items: [],
        pagination: this.pageInfo.toPagination(),
      };
    }

    // Unpack
    const { items, pageInfo } = data;

    // Exists
    if (typeof items !== "object" || items === null) {
      throw new Error("Incorrect data");
    }

    // Get page info
    if (pageInfo !== undefined) {
      this.pageInfo.fromObject(pageInfo);
    }

    // Build
    const dtoList = [] as Dto[];
    items.forEach((entity: JSONData) => {
      // Create dto
      const dto = this._buildInstance(entity);

      // Reinsert into list
      this.reinsert(dto);
      dtoList.push(dto);
    });

    return {
      items: dtoList,
      pagination: this.pageInfo.toPagination(),
    };
  }

  async update(entities?: Dto[] | undefined): Promise<void> {
    // Get entities
    if (entities === undefined) {
      entities = this.list;
    }

    // Loop through entities
    await Promise.all(
      entities.map(async (entity: Dto) => {
        await entity.update(this._pathVars);
      }),
    );
  }

  async search(
    searchQuery: string,
    pagination?: Pagination,
  ): Promise<{
    items: Dto[];
    pagination: IPagination;
  }> {
    // Build path
    const path = this.buildPath();

    // Set pagination
    if (pagination !== undefined) {
      this.pageInfo.fromPagination(pagination);
    }

    // Get data
    const data: DataListResponse =
      (await request({
        url:
          path +
          "?search=" +
          searchQuery +
          "&pagination=" +
          this.pageInfo.toQueryParams(),
        method: "get",
      })) ?? [];

    // Handle null response (e.g., from 401 errors)
    if (data === null) {
      console.info("Request returned null, likely due to authentication error");
      return {
        items: [],
        pagination: this.pageInfo.toPagination(),
      };
    }

    // Unpack
    const { items, pageInfo } = data;
    if (data.active !== undefined) {
      this.active = data.active;
    }

    // Exists
    if (typeof items !== "object" || items === null) {
      throw new Error("Incorrect data");
    }

    // Get page info
    if (pageInfo !== undefined) {
      this.pageInfo.fromObject(pageInfo);
    }

    // Build
    const dtoList = [] as Dto[];
    items.forEach((entity: JSONData) => {
      // Create dto
      const dto = this._buildInstance(entity);

      // Reinsert into list
      this.reinsert(dto);
      dtoList.push(dto);
    });

    return {
      items: dtoList,
      pagination: this.pageInfo.toPagination(),
    };
  }

  private reinsert(entity: Dto): void {
    // Find entity
    const index = this.list.findIndex((e) => e.id === entity.id);

    // Reinsert
    if (index === -1) {
      this.list.push(entity);
    } else {
      this.list[index] = entity;
    }
  }

  _buildInstance(data: JSONData): Dto {
    return new Dto(data);
  }
}
